import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { usePortal } from '@/contexts/PortalContext'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Eye, EyeOff } from 'lucide-react'
import api from '@/services/api'
import { authService } from '@/services/auth'
import { idealPostcodesService, type Address } from '@/services/idealPostcodes'

export default function PortalRegister() {
  const { portal } = usePortal()
  const navigate = useNavigate()
  const primaryColor = portal?.primary_color || '#0ea5e9'

  const [form, setForm] = useState({
    title: 'MR',
    first_name: '', last_name: '', email: '', password: '', confirm_password: '',
    mobile_number: '', postcode: '',
    address_line1: '', address_line2: '', city: '', county: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Postcode → address lookup
  const [addresses, setAddresses] = useState<Address[]>([])
  const [addrLoading, setAddrLoading] = useState(false)
  const [addrError, setAddrError] = useState<string | null>(null)
  const [showPw, setShowPw] = useState(false)
  const [showConfirmPw, setShowConfirmPw] = useState(false)

  // OTP verification (mobile then email) after registration
  const [verifyStep, setVerifyStep] = useState<'mobile' | 'email' | null>(null)
  const [regEmail, setRegEmail] = useState('')
  const [regMobile, setRegMobile] = useState('')
  const [otpVia, setOtpVia] = useState('sms')
  const [otpCode, setOtpCode] = useState('')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [otpInfo, setOtpInfo] = useState<string | null>(null)
  const [otpLoading, setOtpLoading] = useState(false)

  const verifyOtp = async () => {
    setOtpError(null)
    if (otpCode.trim().length < 4) { setOtpError('Enter the 6-digit code'); return }
    setOtpLoading(true)
    try {
      if (verifyStep === 'mobile') {
        await authService.verifyMobile({ email: regEmail, code: otpCode.trim() } as any)
        setOtpCode(''); setOtpInfo(null); setVerifyStep('email')
      } else {
        await authService.verifyEmail({ email: regEmail, code: otpCode.trim() } as any)
        setVerifyStep(null); setSuccess(true)
        setTimeout(() => navigate('/login'), 2000)
      }
    } catch (err: any) {
      setOtpError(err?.response?.data?.detail || 'Invalid or expired code')
    } finally {
      setOtpLoading(false)
    }
  }

  const resendOtp = async () => {
    setOtpError(null); setOtpInfo(null)
    try {
      await authService.resendVerification(regEmail, verifyStep === 'email' ? 'email' : 'mobile')
      setOtpInfo('A new code has been sent.')
    } catch (err: any) {
      setOtpError(err?.response?.data?.detail || 'Could not resend code')
    }
  }

  const findAddress = async () => {
    setAddrError(null)
    setAddresses([])
    if (!form.postcode.trim()) { setAddrError('Enter a postcode first'); return }
    setAddrLoading(true)
    const res = await idealPostcodesService.lookupPostcode(form.postcode.trim())
    setAddrLoading(false)
    if (!res.success || !res.addresses || res.addresses.length === 0) {
      setAddrError(res.message || 'No addresses found for that postcode')
      return
    }
    setAddresses(res.addresses)
  }

  const selectAddress = (index: number) => {
    const a = addresses[index]
    if (!a) return
    setForm(f => ({
      ...f,
      address_line1: a.line_1 || '',
      address_line2: a.line_2 || '',
      city: a.post_town || '',
      county: a.county || '',
      postcode: a.postcode || f.postcode,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await api.post('/portal/register', {
        subdomain: portal?.subdomain,
        title: form.title,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        mobile_number: form.mobile_number,
        postcode: form.postcode,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        county: form.county,
      })
      // Account is created inactive — verify mobile then email by OTP before logging in.
      if (res.data?.requires_verification) {
        setRegEmail(form.email)
        setRegMobile(res.data.mobile_number || form.mobile_number)
        setOtpVia(res.data.otp_via || 'sms')
        setVerifyStep('mobile')
      } else {
        setSuccess(true)
        setTimeout(() => navigate('/login'), 2000)
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {portal?.logo_url && (
          <div className="text-center mb-8">
            <img src={portal.logo_url} alt={portal.company_name} className="h-16 w-auto mx-auto" />
          </div>
        )}
        <Card>
          <div className="p-6 sm:p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Create Account</h1>
            <p className="text-gray-500 text-sm mb-6">
              {portal?.welcome_message || `Join ${portal?.company_name || 'us'} today`}
            </p>

            {verifyStep ? (
              <div className="space-y-4">
                <div className="text-xs font-medium text-gray-400">Step {verifyStep === 'mobile' ? '1' : '2'} of 2</div>
                <h2 className="text-lg font-bold text-gray-900">
                  {verifyStep === 'mobile' ? 'Verify your mobile' : 'Verify your email'}
                </h2>
                <p className="text-sm text-gray-600">
                  {verifyStep === 'mobile'
                    ? <>We sent a 6-digit code to <strong>{regMobile}</strong> via {otpVia === 'whatsapp' ? 'WhatsApp' : 'SMS'}.</>
                    : <>We sent a 6-digit code to <strong>{regEmail}</strong>.</>}
                </p>
                {otpError && <Alert variant="error">{otpError}</Alert>}
                {otpInfo && <Alert variant="success">{otpInfo}</Alert>}
                <Input
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  inputMode="numeric"
                  className="text-center tracking-[0.5em] text-lg"
                />
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={otpLoading}
                  className="w-full py-3 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {otpLoading ? 'Verifying…' : (verifyStep === 'mobile' ? 'Verify mobile' : 'Verify email')}
                </button>
                <p className="text-center text-sm text-gray-500">
                  Didn't get it?{' '}
                  <button type="button" onClick={resendOtp} className="font-medium hover:underline" style={{ color: primaryColor }}>Resend code</button>
                </p>
              </div>
            ) : success ? (
              <Alert variant="success">Account verified! Redirecting to login...</Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && <Alert variant="error">{error}</Alert>}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <select
                    value={form.title}
                    onChange={e => setForm({...form, title: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-[42px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="MR">Mr</option>
                    <option value="MRS">Mrs</option>
                    <option value="MS">Ms</option>
                    <option value="MISS">Miss</option>
                    <option value="DR">Dr</option>
                    <option value="REV">Rev</option>
                    <option value="PROF">Prof</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <Input type="tel" value={form.mobile_number} onChange={e => setForm({...form, mobile_number: e.target.value})} placeholder="07…" required />
                </div>

                {/* Postcode + address finder */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <div className="flex gap-2">
                    <Input
                      value={form.postcode}
                      onChange={e => setForm({...form, postcode: e.target.value})}
                      placeholder="e.g. S40 1AB"
                    />
                    <button
                      type="button"
                      onClick={findAddress}
                      disabled={addrLoading || !form.postcode.trim()}
                      className="px-4 rounded-lg text-white text-sm font-medium whitespace-nowrap disabled:opacity-50 h-[42px]"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {addrLoading ? 'Searching…' : 'Find address'}
                    </button>
                  </div>
                  {addrError && <p className="text-xs text-red-600 mt-1">{addrError}</p>}
                  {addresses.length > 0 && (
                    <select
                      className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      defaultValue=""
                      onChange={e => { if (e.target.value !== '') selectAddress(Number(e.target.value)) }}
                    >
                      <option value="" disabled>Select your address ({addresses.length} found)…</option>
                      {addresses.map((a, i) => (
                        <option key={i} value={i}>{a.formatted || `${a.line_1}, ${a.post_town}`}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                  <Input value={form.address_line1} onChange={e => setForm({...form, address_line1: e.target.value})} placeholder="House / street" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2 <span className="text-gray-400 font-normal">(optional)</span></label>
                  <Input value={form.address_line2} onChange={e => setForm({...form, address_line2: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Town / City</label>
                    <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">County <span className="text-gray-400 font-normal">(optional)</span></label>
                    <Input value={form.county} onChange={e => setForm({...form, county: e.target.value})} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} required className="pr-10" />
                    <button type="button" tabIndex={-1} onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <div className="relative">
                    <Input type={showConfirmPw ? 'text' : 'password'} value={form.confirm_password} onChange={e => setForm({...form, confirm_password: e.target.value})} required className="pr-10" />
                    <button type="button" tabIndex={-1} onClick={() => setShowConfirmPw(v => !v)} aria-label={showConfirmPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
                <p className="text-center text-sm text-gray-500">
                  Already have an account?{' '}
                  <Link to="/login" className="font-medium hover:underline" style={{ color: primaryColor }}>Sign in</Link>
                </p>
              </form>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
